import { NgClass } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  ViewRef,
  inject,
} from '@angular/core';
import { CompiereDataGridRequestJSON } from '@compiere-ws/models/compiere-data-json';
import { ViewType } from '@iupics-components/models/view-type.enum';
import { AppConfig } from '@iupics-config/app.config.service';
import { UnsavedChangesService } from '@iupics-manager/managers/unsaved-changes/unsaved-changes.service';
import { Global } from '@iupics-manager/models/global-var';
import { listenWindowOutsideAngular } from '@iupics-util/tools/zone-listener.util';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Tab } from '@web-desktop/models/Tab';
import { IupicsMenuType } from '@web-desktop/models/menu-item-ui';
import { TooltipModule } from 'primeng/tooltip';
import { KeybindingDirective } from '../../../../../iupics-components/directives/keybinding.directive';
import { ColumnState } from '@iupics/apiz-grid';

@Component({
  selector: 'iu-tab-ui',
  templateUrl: './tab-ui.component.html',
  styleUrls: ['./tab-ui.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [TooltipModule, NgClass, KeybindingDirective, TranslateModule],
})
export default class TabUiComponent implements OnInit, Tab {
  static cpt = 0;

  @ViewChild('tab', { static: true })
  ref: ElementRef;
  @ViewChild('removeBtn', { static: true })
  removeBtn: ElementRef;

  actionID: number;
  angularClass?: string;
  appName: string;
  ctx: any;
  initGridParams: { request: CompiereDataGridRequestJSON; state: ColumnState[] };
  domWinId: string;
  iconClass: string;
  id: string;
  isActive = false;
  isLoading = false;
  menuType: IupicsMenuType;
  name: string;
  openedRecordId: string;
  othersRecordId: { tabId: string; recordId: string }[];
  parentId: string;
  processParamsMap: Map<string, any>;
  splitViewRef: ViewRef = null;
  tabWidth: number;
  viewType: ViewType;
  windowId: number;
  zoomInfo: any;

  destroyTabEmitter: EventEmitter<any> = new EventEmitter();
  setActiveTabEmitter: EventEmitter<any> = new EventEmitter();
  closeWindowEmitter: EventEmitter<any> = new EventEmitter();
  moveTabEmitter: EventEmitter<any> = new EventEmitter();
  createTabContainerEmitter: EventEmitter<any> = new EventEmitter();
  /** right click or menu key on the tab: the tab bar shows its menu (close others, all...) */
  contextMenuEmitter: EventEmitter<{ tab: TabUiComponent; x: number; y: number }> = new EventEmitter();

  /** Unsaved edit-view changes in this window (signal: no polling). */
  readonly unsavedChanges = inject(UnsavedChangesService);

  constructor(
    private translateService: TranslateService,
    private config: AppConfig
  ) {
    // one resize listener per tab: outside Angular, enters only when the width actually changes
    listenWindowOutsideAngular(
      'resize',
      () => this.#computeTabWidth() !== this.tabWidth,
      () => (this.tabWidth = this.#computeTabWidth())
    );
  }

  #computeTabWidth(): number {
    return Global.isMobileWidth()
      ? this.config.getConstant('TabUiComponent#tabWidth_mobile')
      : this.config.getConstant('TabUiComponent#tabWidth_not-mobile');
  }

  ngOnInit() {
    this.tabWidth = this.#computeTabWidth();
  }

  /* methods used by tabs*/
  handleDragStart(ev: DragEvent): void {
    ev.dataTransfer.setData('fireEvent', 'draggend');
    Global.draggedComponent = this;
    if(this.ref?.nativeElement.parentElement){
        this.ref?.nativeElement.parentElement.classList.add('dragged');
    }
    ev.stopPropagation();
  }

  handleDragEnd(ev: DragEvent): void {
      if(this.ref?.nativeElement?.parentElement){
          this.ref?.nativeElement.parentElement.classList.remove('dragged');
      }
    Global.draggedComponent = null;
  }

  handleDrop(ev: DragEvent): boolean {
    ev.preventDefault();
    if (Global.draggedComponent === null || Global.draggedComponent.id.startsWith('tabContainer')) {
      return false;
    } else {
      // cannot drop in current tab if it's inside a tab container or dragged tab is from a tab container or dragged tab is dropped on itself
      if (this.id !== Global.draggedComponent.id && !this.parentId.startsWith('tabContainer')) {
        ev.stopPropagation();
        this.createTabContainerEmitter.emit({ draggedTab: Global.draggedComponent, droppedInTab: this });
        Global.draggedComponent = null;
      }
    }
  }

  handleDragOver(ev: any): boolean {
    // drop zone is the dragged component itself
    ev.preventDefault();
    if (Global.draggedComponent === null) {
      return false;
    }
    if (
      Global.draggedComponent.id !== this.id &&
      !this.parentId.startsWith('tabContainer') &&
      (Global.draggedComponent.id.startsWith('tabContainer') ||
        !Global.draggedComponent.parentId.startsWith('tabContainer'))
    ) {
      // checking on which side dropzone should be displayed
      const bounding = Global.draggedComponent.ref?.nativeElement.getBoundingClientRect();
      const boundingOver = this.ref?.nativeElement.getBoundingClientRect();
      const x = (ev.layerX / this.ref?.nativeElement.clientWidth) * 100;
      if (bounding.x > boundingOver.x && x < 40) {
        this.moveTabEmitter.emit({ tab: this, move: 0 });
      }
      if (bounding.x < boundingOver.x && x > 60) {
        this.moveTabEmitter.emit({ tab: this, move: 0 });
      }
    }
  }

  setActive(ev?: MouseEvent): void {
    if (!this.isLoading && !this.isActive) {
      this.setActiveTabEmitter.emit(this);
    }
  }

  handleRemove(): void {
    if (this.name !== this.translateService.instant('tabUi.loading')) {
      this.closeWindowEmitter.emit({ tabId: this.id, tabUI: this });
    }
  }

  destroyTab(): void {
    this.destroyTabEmitter.emit(this);
  }

  /** keyboard: Enter / Space opens, Delete closes, arrows move between tabs, menu key opens the tab menu */
  onKeydown(event: KeyboardEvent): void {
    const li = this.ref?.nativeElement as HTMLElement;
    if (event.target !== li) return;
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.setActive();
        break;
      case 'Delete':
        event.preventDefault();
        this.handleRemove();
        break;
      case 'ArrowLeft':
      case 'ArrowRight': {
        event.preventDefault();
        const tabs = Array.from(li.closest('.tabsBar')?.querySelectorAll<HTMLElement>('li.tab') ?? []);
        const index = tabs.indexOf(li) + (event.key === 'ArrowLeft' ? -1 : 1);
        tabs[index]?.focus();
        tabs[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        break;
      }
      case 'ContextMenu':
        event.preventDefault();
        this.#emitContextMenu();
        break;
      case 'F10':
        if (event.shiftKey) {
          event.preventDefault();
          this.#emitContextMenu();
        }
        break;
    }
  }

  openContextMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.contextMenuEmitter.emit({ tab: this, x: event.clientX, y: event.clientY });
  }

  #emitContextMenu(): void {
    const rect = (this.ref?.nativeElement as HTMLElement)?.getBoundingClientRect();
    if (rect) {
      this.contextMenuEmitter.emit({ tab: this, x: rect.left, y: rect.bottom });
    }
  }

  handleKeybind(event: KeyboardEvent): void {
    event.stopPropagation();
  }
}
